/**
 * API module for searching papers by title
 */

import { buildApiHeaders } from './apiUtils';

export interface PaperAuthor {
    name: string;
    authorId?: string;
}

export interface PaperSuggestion {
    paperId: string;
    corpusId: number;
    title: string;
    year?: number;
    venue?: string;
    authors?: PaperAuthor[];
}

export interface PaperSearchResponse {
    suggestions: PaperSuggestion[];
}

export interface PaperValidationResponse {
    corpus_id: string;
    searchable: boolean | null;
    error?: string;
}

/**
 * Search for papers by partial title query
 * 
 * @param query - Partial title string typed by user
 * @param limit - Maximum number of suggestions to return (default 10)
 * @returns Promise resolving to array of paper suggestions
 */
export async function searchPapersByTitle(
    query: string,
    limit: number = 10
): Promise<PaperSuggestion[]> {
    if (!query || !query.trim()) {
        return [];
    }

    const params = new URLSearchParams({
        query: query.trim(),
        limit: limit.toString(),
    });

    const headers = await buildApiHeaders();
    const response = await fetch(`/api/paper-title-search?${params.toString()}`, {
        headers,
    });
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Search failed with status ${response.status}`);
    }

    const data: PaperSearchResponse = await response.json();
    return data.suggestions || [];
}

/**
 * Validate a paper title by resolving it to a corpus_id and checking searchability
 * 
 * @param paperTitle - The paper title to validate
 * @returns Promise resolving to validation response with corpus_id and searchability status
 */
export async function validatePaperTitle(
    paperTitle: string
): Promise<PaperValidationResponse> {
    if (!paperTitle || !paperTitle.trim()) {
        throw new Error('Paper title is required');
    }

    const headers = await buildApiHeaders();
    const response = await fetch('/api/validate-paper-title', {
        method: 'POST',
        headers,
        body: JSON.stringify({ paper_title: paperTitle.trim() }),
    });
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Validation failed with status ${response.status}`);
    }

    const data: PaperValidationResponse = await response.json();
    return data;
}

